/**
 * Admin Notifications — Round 26 (Sep 17 2026)
 * Every admin notification on one page (the bell only shows the last few and
 * is awkward on a phone): workforce escalations, reports, tickets, appeals.
 * Tapping a row marks it read and opens the thing it is about — an
 * escalation lands on the agent's page with the escalation panel open,
 * showing what went wrong, what the agent did, and whether it still needs a
 * person.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdmin, notificationTarget } from '../AdminApp';
import { toast } from 'sonner';
import { Bell, RefreshCw, CheckCheck, ChevronRight, X, Trash2 } from 'lucide-react';

// Round 40: the bell, the sidebar badge and this page refresh together.
export const ADMIN_NOTIFS_CHANGED = 'gems:admin-notifications-changed';
const announce = () => { try { window.dispatchEvent(new Event(ADMIN_NOTIFS_CHANGED)); } catch (e) { /* noop */ } };

const ICONS = {
  ban_appeal: '⚠️', new_report: '🚩', stream_report: '🎥', new_support_ticket: '🎫', new_user: '👤',
  workforce_escalation: '🤖', fraud_alert: '💳', payout_request: '💸', app_crash: '🐞', system: '🔔',
};
const SEV = { critical: 'bg-red-100 text-red-800', high: 'bg-orange-100 text-orange-800', medium: 'bg-amber-100 text-amber-800', low: 'bg-blue-100 text-blue-800' };

const ago = (iso) => {
  if (!iso) return '';
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (isNaN(d)) return '';
  if (d < 60) return 'just now';
  if (d < 3600) return `${Math.round(d / 60)}m ago`;
  if (d < 86400) return `${Math.round(d / 3600)}h ago`;
  return `${Math.round(d / 86400)}d ago`;
};

export default function AdminNotificationsPage() {
  const { api } = useAdmin();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');   // all | unread | workforce

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/admin/notifications?limit=50');
      setItems(r.data.notifications || []);
      setUnread(r.data.unread_count || 0);
    } catch (e) { toast.error('Could not load notifications'); }
    finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener(ADMIN_NOTIFS_CHANGED, h);
    return () => window.removeEventListener(ADMIN_NOTIFS_CHANGED, h);
  }, [load]);

  const open = async (n) => {
    if (!n.read) {
      try { await api.post(`/api/admin/notifications/${n.id}/read`); } catch (e) { /* still navigate */ }
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      setUnread((u) => Math.max(0, u - 1));
    }
    navigate(notificationTarget(n));
  };
  const markAll = async () => {
    try { await api.post('/api/admin/notifications/mark-all-read'); setItems((prev) => prev.map((x) => ({ ...x, read: true }))); setUnread(0); toast.success('All marked as read'); announce(); }
    catch (e) { toast.error('Could not mark all as read'); }
  };
  // Round 40: clear one / everything read / everything
  const clearOne = async (n) => {
    setItems((prev) => prev.filter((x) => x.id !== n.id));
    if (!n.read) setUnread((u) => Math.max(0, u - 1));
    try { await api.delete(`/api/admin/notifications/${n.id}`); announce(); }
    catch (e) { toast.error('Could not clear it'); load(); }
  };
  const clearMany = async (scope) => {
    if (scope === 'all' && !window.confirm('Clear every notification? They leave the list and the bell for all admins.')) return;
    try {
      const r = await api.post('/api/admin/notifications/clear', { scope });
      toast.success(`Cleared ${r.data?.cleared ?? 0}`);
      announce();
      load();
    } catch (e) { toast.error('Could not clear notifications'); }
  };
  const readCount = items.filter((x) => x.read).length;

  const shown = items.filter((n) => filter === 'all' ? true : filter === 'unread' ? !n.read : n.type === 'workforce_escalation');

  return (
    <div className="space-y-4" data-testid="admin-notifications-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Bell className="w-6 h-6 text-emerald-600" /> Notifications</h1>
          <p className="text-gray-500 text-sm mt-1">{unread} unread · tap one to open what it's about</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="flex items-center gap-1 px-3 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh</button>
          {unread > 0 && <button onClick={markAll} className="flex items-center gap-1 px-3 py-2 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700"><CheckCheck className="w-4 h-4" /> Mark all read</button>}
          {readCount > 0 && <button onClick={() => clearMany('read')} className="flex items-center gap-1 px-3 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50" data-testid="admin-notif-clear-read"><X className="w-4 h-4" /> Clear read</button>}
          {items.length > 0 && <button onClick={() => clearMany('all')} className="flex items-center gap-1 px-3 py-2 border border-rose-200 text-rose-700 rounded-lg text-sm hover:bg-rose-50" data-testid="admin-notif-clear-all"><Trash2 className="w-4 h-4" /> Clear all</button>}
        </div>
      </div>

      <div className="flex gap-2">
        {[['all', 'All'], ['unread', 'Unread'], ['workforce', 'AI Workforce']].map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${filter === k ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{label}</button>
        ))}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 divide-y divide-gray-100">
        {loading && items.length === 0 ? (
          <div className="p-8 text-center text-gray-400 text-sm">Loading…</div>
        ) : shown.length === 0 ? (
          <div className="p-8 text-center text-gray-400 text-sm">Nothing here.</div>
        ) : shown.map((n) => (
          <div key={n.id} className={`flex items-stretch ${!n.read ? 'bg-emerald-50/40' : ''}`}>
          <button onClick={() => open(n)} className="flex-1 min-w-0 text-left p-4 flex gap-3 hover:bg-gray-50" data-testid={`admin-notif-${n.id}`}>
            <span className="text-xl leading-none mt-0.5">{ICONS[n.type] || '📢'}</span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-sm ${!n.read ? 'font-semibold text-gray-900' : 'text-gray-800'}`}>{n.title || n.subject || n.type?.replace(/_/g, ' ') || 'Notification'}</span>
                {n.severity && <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold uppercase ${SEV[n.severity] || 'bg-gray-100 text-gray-600'}`}>{n.severity}</span>}
                {n.type === 'workforce_escalation' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 font-semibold">ADMIN EVENT</span>}
              </div>
              {(n.message || n.user_name) && <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">{n.message || `from ${n.user_name}`}</p>}
              {n.suggested_action && <p className="text-xs text-emerald-700 mt-0.5"><span className="font-semibold">Suggested: </span>{n.suggested_action}</p>}
              <p className="text-[11px] text-gray-400 mt-1">{ago(n.created_at)}{n.agent ? ` · ${n.agent}` : ''}</p>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-300 mt-1 shrink-0" />
          </button>
          <button onClick={() => clearOne(n)} title="Clear" className="px-3 text-gray-300 hover:text-rose-600 hover:bg-rose-50" data-testid={`admin-notif-clear-${n.id}`}><X className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
